import React, { useMemo } from "react";
import { Form } from "antd";
import { FormInstance } from "antd";
import OrderDTO from "@/dto/order/OrderDTO";
import ShippingAddressDTO from "@/dto/ShippingAddressDTO";
import { observer } from "mobx-react-lite";
import { MultiShopSelectPlatformType } from "@/components/MultiShopSelect/types";
import WaybillAlert from "./WaybillAlert";
import WaybillTemplateFormItems from "./WaybillTemplateFormItems";
import GetWaybillNoMethod from "@/scenes/OrderManagement/components/dialogs/PrintWaybillDialog/GetWaybillNoMethod";
import WaybillNoMethodFormItem from "./WaybillNoMethodFormItem";
import WaybillTemplateDTO from "@/dto/template/WaybillTemplateDTO";
import CopiesFormItems from "./CopiesFormItems";
import { PlatformOrderInfo } from ".";
import ShippingAddressFormItem from "./ShippingAddressFormItem";
import { PrintWaybillAdvancedSetting } from "@/scenes/OrderManagement/components/dialogs/PrintWaybillDialog/components/AdvancedSettingFormItem";
import AdvancedSettingFormItem from "./AdvancedSettingFormItem";
import { EwaybillCopiesPreset } from "@/components/EwaybillCopiesSelector";
import type SupplierPartnershipsStore from "@/store/SupplierPartnershipsStore";

const formItemLayout = {
    labelCol: {
        sm: { span: 4 },
    },
    wrapperCol: {
        sm: { span: 20 },
    },
};

/**
 *  表单数据
 */
export interface PrintWaybillFormValues {
    getWaybillNoMethod: GetWaybillNoMethod;

    shippingAddress: ShippingAddressDTO;

    copies?: EwaybillCopiesPreset;

    isGenerateWaybillNoOnly?: boolean;

    printGiftInLastWaybill?: boolean;

    specifiedSplitQuantity?: number;

    maxWeightPerPackage?: number;
    defaultUnitWeight?: number;

    [MultiShopSelectPlatformType.tb]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.pdd]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.dy]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.jd]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.ks]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.ali]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.xhs]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.channelShop]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.wb]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.other]: PlatformSelectedTemplateInfos;

    [MultiShopSelectPlatformType.other_drop_shipping_supplier_ks]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.other_drop_shipping_supplier_xhs]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.other_drop_shipping_supplier_channel_shop]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.other_drop_shipping_supplier_wb]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.other_drop_shipping_supplier_other]: PlatformSelectedTemplateInfos;

    [MultiShopSelectPlatformType.ali_out_order_taobao]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.ali_out_order_pdd]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.ali_out_order_dy]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.ali_out_order_kwai]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.ali_out_order_xhs]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.ali_out_order_jd]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.ali_out_order_weixin]: PlatformSelectedTemplateInfos;

    [MultiShopSelectPlatformType.ali_plaintext_out_order_taobao]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.ali_plaintext_out_order_pdd]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.ali_plaintext_out_order_dy]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.ali_plaintext_out_order_kwai]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.ali_plaintext_out_order_xhs]: PlatformSelectedTemplateInfos;
    [MultiShopSelectPlatformType.ali_plaintext_out_order_jd]: PlatformSelectedTemplateInfos;

    /**
     * 高级选项参数(目前不显示出来)
     */
    advancedSetting?: PrintWaybillAdvancedSetting;

    shippingAddressMatchRule?: ShippingAddressMatchRule;
}

export enum ShippingAddressMatchRule {
    SHOP = "SHOP",
    TEMPLATE = "TEMPLATE",
}

/**
 * 平台店铺选择模板 打印机 是否子母单相关
 */
export interface PlatformSelectedTemplateInfos {
    template: WaybillTemplateDTO;

    printerName: string;

    useChildWaybillNoMode: boolean;
}

interface PrintWaybillFormProps {
    shippingAreaBindTemplateId?: number;
    orders: OrderDTO[];
    defaultTemplateId?: number;
    form?: FormInstance<PrintWaybillFormValues>;
    platformOrderInfo: PlatformOrderInfo;
    shopId?: number;
    showProcessInRange?: boolean;
    supplierPartnershipsStore?: SupplierPartnershipsStore;
}

const PrintWaybillForm: React.FC<PrintWaybillFormProps> = (props) => {
    const {
        form,
        orders,
        defaultTemplateId,
        platformOrderInfo,
        shopId,
        showProcessInRange,
        supplierPartnershipsStore,
    } = props;

    const [realForm] = Form.useForm<PrintWaybillFormValues>(form);
    const isSomeOrderPrinted = orders.some((order) => order.isWaybillPrinted);

    const firstPlatform = useMemo(
        () =>
            Object.keys(platformOrderInfo).find((platform: MultiShopSelectPlatformType) => {
                return !!platformOrderInfo?.[platform]?.length;
            }) as MultiShopSelectPlatformType,
        [platformOrderInfo],
    );

    return (
        <Form
            {...formItemLayout}
            initialValues={{
                copies: 1,
                getWaybillNoMethod: !isSomeOrderPrinted ? GetWaybillNoMethod.NEW : null,
            }}
            form={realForm}
        >
            <WaybillAlert platformOrderInfo={platformOrderInfo} orders={orders} />

            <WaybillTemplateFormItems platformOrderInfo={platformOrderInfo} defaultTemplateId={defaultTemplateId} />

            <WaybillNoMethodFormItem form={realForm} />

            <ShippingAddressFormItem
                firstPlatform={firstPlatform}
                shopId={shopId}
                form={form}
                orders={orders}
                supplierPartnershipsStore={supplierPartnershipsStore}
            />

            <CopiesFormItems orders={orders} />

            <AdvancedSettingFormItem form={realForm} showProcessInRange={showProcessInRange} />
        </Form>
    );
};

export default observer(PrintWaybillForm);
